import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { resolveUrl } from "@/components/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/clients`);
        if (!cancel) setClients(data.clients || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <>
      <section className="relative pt-40 pb-16 md:pt-52 md:pb-20 border-b border-white/5" data-testid="clients-hero">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-6">
              <span className="text-white/35 mr-3">CLIENTS / 00</span>Partners
            </div>
            <h1 className="font-display text-6xl md:text-[8vw] leading-[0.92] tracking-[-0.03em] text-white max-w-5xl">
              The venues we<br />
              call <span className="font-serif-display text-[var(--gold)]">home</span>.
            </h1>
            <p className="mt-10 max-w-2xl text-white/65 text-lg leading-relaxed">
              A growing roster of restaurants, lounges, and venues that host Lucra on their signature nights.
              Tap any mark to open their gallery.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          {loading && (
            <div className="font-mono-tech text-white/40 text-sm" data-testid="clients-loading">Loading roster…</div>
          )}
          {!loading && clients.length === 0 && (
            <div className="text-white/55">No clients yet. Add one from the admin panel.</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {clients.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 0.08}>
                <div
                  className="group relative bg-[#0a0a0a] p-8 md:p-10 flex flex-col items-center text-center hover:bg-[#111] transition-colors beam-card h-full"
                  data-testid={`client-card-${c.slug}`}
                >
                  <Link to={`/clients/${c.slug}`} className="block w-full">
                    <div className="h-40 md:h-48 w-full flex items-center justify-center mb-6">
                      <img
                        src={resolveUrl(c.logo_url)}
                        alt={c.name}
                        loading="lazy"
                        className="max-h-full max-w-[80%] object-contain opacity-95 group-hover:opacity-100 transition-opacity"
                        style={{ filter: "drop-shadow(0 0 18px rgba(212,175,55,0.15))" }}
                      />
                    </div>
                    <div className="font-display text-2xl md:text-3xl text-white tracking-tight">{c.name}</div>
                    {c.location && (
                      <div className="mt-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[var(--gold)]">
                        {c.location}
                      </div>
                    )}
                    <div className="mt-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/40">
                      {(c.photos || []).length} {(c.photos || []).length === 1 ? "frame" : "frames"}
                    </div>
                  </Link>
                  <Link
                    to={`/clients/${c.slug}`}
                    className="btn-gold mt-6"
                    data-testid={`client-view-gallery-${c.slug}`}
                  >
                    <span>View Gallery</span>
                    <ArrowUpRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-white/5">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <h3 className="font-display text-3xl md:text-5xl text-white tracking-tight max-w-2xl">
            Want your venue on this <span className="font-serif-display text-[var(--gold)]">list</span>?
          </h3>
          <Link to="/contact" className="btn-gold" data-testid="clients-cta"><span>Partner with Lucra</span><ArrowUpRight size={14} /></Link>
        </div>
      </section>
    </>
  );
};

export default Clients;
